import axios from 'axios';

// ======================== Configuration ========================
const BASE_URL = 'https://loyalty.bom.asia';

const apiClient = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

// Interceptors
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      console.warn('⚠️  API CheckIn - NO TOKEN FOUND in localStorage!');
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ======================== User CheckIn Management Functions ========================

/**
 * Tạo người dùng mới với thông tin cơ bản
 * @route POST /api/miniApp/checkin/users
 * @param {Object} userData - Thông tin người dùng
 * @param {string} userData.name - Tên người dùng (required)
 * @param {string} userData.userId - User ID của Zalo (required)
 * @param {string} userData.phone - Số điện thoại
 * @param {string} userData.avatar - Link ảnh Zalo
 * @param {string} userData.idByOA - ID theo OA
 * @param {boolean} userData.followedOA - Follow OA
 * @param {boolean} userData.isSensitive - Dữ liệu nhạy cảm
 */
export const createUserPhone = async (userData) => {
  try {
    const response = await apiClient.post('/api/miniApp/checkin/users', userData);
    return response.data;
  } catch (error) {
    console.error('Error creating user:', error);
    throw error.response?.data || { message: 'Failed to create user' };
  }
};

/**
 * Lấy danh sách users với pagination và filter
 * @route GET /api/miniApp/checkin/users
 * @param {Object} params - Query parameters
 * @param {number} params.page - Trang hiện tại (default: 1)
 * @param {number} params.limit - Số lượng mỗi trang (default: 10)
 * @param {string} params.name - Tìm kiếm theo tên (optional)
 * @param {string} params.userId - Tìm kiếm theo userId (optional)
 * @param {string} params.phone - Tìm kiếm theo SĐT (optional)
 * @param {string} params.role_code - Lọc theo vai trò (optional)
 * @param {boolean} params.followedOA - Lọc theo trạng thái follow OA (optional)
 * @param {number} params.eventId - Lọc theo event ID (optional)
 * 
 * @example
 * // Tìm kiếm theo tên
 * await getUsers({ name: 'Nguyễn', page: 1, limit: 10 });
 * 
 * @example
 * // Tìm kiếm theo phone
 * await getUsers({ phone: '0123', page: 1, limit: 10 });
 * 
 * @example
 * // Filter theo event và tên
 * await getUsers({ eventId: 1, name: 'Nguyễn', page: 1, limit: 10 });
 */
export const getUsers = async (params = {}) => {
  try {
    const response = await apiClient.get('/api/miniApp/checkin/users', { params });
    return response.data || [];
  } catch (error) {
    console.error('Error fetching users:', error);
    throw error.response?.data || { message: 'Failed to fetch users' };
  }
};

/**
 * Kiểm tra userId có tồn tại không
 * @route GET /api/miniApp/checkin/users/check
 * @param {string} userId - User ID cần kiểm tra (required)
 * @example checkUserId('user123')
 */
export const checkUserId = async (userId) => {
  try {
    const response = await apiClient.get('/api/miniApp/checkin/users/check', {
      params: { userId }
    });
    return response.data;
  } catch (error) {
    console.error('Error checking userId:', error);
    throw error.response?.data || { message: 'Failed to check userId' };
  }
};

/**
 * Lấy thông tin chi tiết user theo userId
 * @route GET /api/miniApp/checkin/users/:userId
 * @param {string} userId - User ID của Zalo
 * @returns {Object} User info với eventRegistrations và eventCheckins
 */
export const getUserByUserId = async (userId) => {
  try {
    const response = await apiClient.get(`/api/miniApp/checkin/users/${userId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching user by userId:', error);
    throw error.response?.data || { message: 'Failed to fetch user' };
  }
};

/**
 * Cập nhật thông tin user (tổng quát)
 * @route PUT /api/miniApp/checkin/users/:userId
 * @param {string} userId - User ID của Zalo
 * @param {Object} updateData - Các fields cần update (trừ id, userId, timestamps)
 * @note Không thể update userId, id, createdAt, updatedAt
 */
export const updateUser = async (userId, updateData) => {
  try {
    const response = await apiClient.put(`/api/miniApp/checkin/users/${userId}`, updateData);
    return response.data;
  } catch (error) {
    console.error('Error updating user:', error);
    throw error.response?.data || { message: 'Failed to update user' };
  }
};

/**
 * Cập nhật thông tin nhà thuốc (pharmacyName và address)
 * @route PUT /api/miniApp/checkin/users/:userId/pharmacy
 * @param {string} userId - User ID của Zalo
 * @param {Object} pharmacyData - Thông tin nhà thuốc
 * @param {string} pharmacyData.pharmacyName - Tên nhà thuốc
 * @param {Array} pharmacyData.address - Mảng các địa chỉ nhà thuốc
 * @example 
 * updatePharmacyInfo('user123', {
 *   pharmacyName: "Nhà thuốc ABC",
 *   address: [{
 *     type: "main",
 *     houseNumber: "123",
 *     city: "Hà Nội",
 *     district: "Cầu Giấy",
 *     ward: "Dịch Vọng",
 *     street: "Xuân Thủy",
 *     isDefault: true
 *   }]
 * })
 */
export const updatePharmacyInfo = async (userId, pharmacyData) => {
  try {
    const response = await apiClient.put(
      `/api/miniApp/checkin/users/${userId}/pharmacy`, 
      pharmacyData
    );
    return response.data;
  } catch (error) {
    console.error('Error updating pharmacy info:', error);
    throw error.response?.data || { message: 'Failed to update pharmacy info' };
  }
};

/**
 * Xóa user check-in theo userId
 * @route DELETE /api/miniApp/checkin/users/:userId
 * @param {string} userId - ID của user cần xóa
 * @returns {Promise<Object>} Response data từ server
 * 
 * @example
 * // Xóa user có userId = '3369156244687554492'
 * await deleteUserByUserId('3369156244687554492');
 */
export const deleteUserByUserId = async (userId) => {
  try {
    if (!userId) {
      throw { message: 'userId is required' };
    }
    
    const response = await apiClient.delete(`/api/miniApp/checkin/users/${userId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting user:', error);
    throw error.response?.data || { message: 'Failed to delete user' };
  }
};

// ======================== Default Export ========================
export default {
  createUserPhone,
  getUsers,
  checkUserId,
  getUserByUserId,
  updateUser,
  updatePharmacyInfo,
  deleteUserByUserId,
};

